import { type components, UserFacingError } from "@appretio/api";
import { useSerialAsync } from "@appretio/shared";
import { useMutation } from "@tanstack/react-query";
import type { GridApi, RowDataTransaction } from "ag-grid-enterprise";
import { useCallback, useRef, useState } from "react";
import {
  type CubeWindow,
  cutPastWindowEnd,
  extractTransactionalGridData
} from "../../components/cubeDataTable/utils/columnUtils";
import { createRowId } from "../../components/cubeDataTable/utils/rowId";
import {
  applyCellChangesLocally,
  CellEditsRefused,
  type CellRefusal,
  type TemporalCellChange,
  type TemporalGridRow
} from "../../utils/weekly/temporalGridAdapter";
import { type ForecastMetaDataType } from "../useFetchForecastMetaData";
import { type CellEditBatch, useCellEditBatcher } from "./useCellEditBatcher";

type TransactionalGridData = components["schemas"]["TransactionalGridData"];

/** Options for {@link useWorkspaceMutationQueue}. */
export interface UseWorkspaceMutationQueueOptions {
  /** Metadata containing year mapping for grid-data extraction. */
  metaData: ForecastMetaDataType | undefined;
  /** Applied markdown_status filter — drives the local EOP markdown correction. */
  markdownStatus?: string[] | null;
  /**
   * The plan's forecast boundary (`forecast_week_from`) — POTB's scan origin.
   * Not `editable_week_from`: that one is context- and viewer-scoped, so it
   * would disagree with the server's recompute and with what save persists.
   */
  forecastWeekFrom?: number;
  /** The window of the cube the edits will be saved to; the recompute is cut at it. */
  saveCubeWindow?: CubeWindow;
  /**
   * Grid-state retention's capture for the post-cascade rows of one
   * transaction (``GridStateRetention.rowSink``), taken before the cascade
   * is awaited so unsaved edits survive navigation but a stale cascade
   * cannot outlive the data it edited. Absent on a grid with no retention.
   */
  rowSink?: () => (rows: TemporalGridRow[]) => void;
}

/** One cell to write, and the grid row index AG Grid raised it on. */
export interface GridCellChange extends TemporalCellChange {
  /** The row index of the edited cell, when the grid raised the edit. */
  rowIndex?: number | null;
}

/**
 * Where a write reads its rows and lands its recompute: a forecast grid
 * ({@link gridApiTarget}), or a page's own copy of a forecast, such as the
 * range plan's workspace of a style option.
 */
export interface WriteTarget {
  /**
   * The transactional rows of `producer`, as `extractTransactionalGridData`
   * reads them off a grid: the years `yearMapping` keeps, weekly columns only.
   */
  rows(
    producer: string,
    yearMapping?: Record<string, number>
  ): TransactionalGridData[];
  /** Lands a recompute's updated rows. */
  apply(update: TemporalGridRow[]): void;
  /** Puts one edited cell back to its value before the edit. */
  revert(change: GridCellChange): void;
  /** The label a refusal's toast names the column by. */
  columnLabel(colId: string): string;
}

/** The forecast grid behind `api` as a write target. */
export function gridApiTarget(api: GridApi): WriteTarget {
  return {
    rows: (producer, yearMapping) =>
      extractTransactionalGridData(api, producer, yearMapping),
    apply: (update) => applyAndRefresh(api, { update }),
    revert: (change) => revertCell(api, change),
    columnLabel: (colId) => {
      const column = api.getColumn(colId);
      return column
        ? (api.getDisplayNameForColumn(column, "header") ?? colId)
        : colId;
    }
  };
}

/** A batch of cell writes against one target, as the queue takes them. */
export interface GridWrite {
  /** Where the batch reads its rows from and applies its result to. */
  target: WriteTarget;
  /** The cells to write. Every one names the producer whose row receives it. */
  cellChanges: GridCellChange[];
  /**
   * Called once the write has landed in the grid (`true`) or failed (`false`).
   * A copy turns its toast on it; a failure has the mutation's own toast.
   */
  onSettled?: (landed: boolean) => void;
  /**
   * What the planner did, for the title of a refusal's toast; omitted is a
   * typed edit. Every gesture but a copy already wrote the grid, so a refused
   * recompute puts each cell back to its old value. A copy never wrote it, and
   * its `oldValue` reads a blank target as 0.
   */
  kind?: "paste" | "delete" | "copy";
}

/**
 * Serialises cell-edit mutations so rapid edits don't race. Only the most
 * recent queued batch is kept; earlier queued batches are discarded. See
 * {@link useSerialAsync} for the queue semantics. `enqueueWrite` keeps every
 * write instead, for a caller whose writes are independent of each other.
 *
 * A paste or a range delete reaches it as one batch (`useCellEditBatcher`)
 * and runs as one recompute carrying every edited cell.
 *
 * One recompute, not one per row: the KPI pipelines are mutual (a ``GIS1``
 * cascade recomputes ``GS1`` and vice versa), so per-row recomputes would each
 * overwrite the previous row's pasted values. Every change is applied first,
 * then all the triggers cascade in a single pass, as the server does.
 *
 * Grid data is extracted at execution time (not queue time) so that derived
 * state from prior ``applyTransaction`` calls is included — a queued event is
 * a pointer to "process the latest grid state for these cell changes", not a
 * snapshot.
 */
/**
 * Apply a row transaction, then recompute the cells that read OTHER rows.
 *
 * Index / per-SQM / SH% / option-derived cells take their value from a
 * different row (the planning year's), a cross-row dependency ag-Grid's change
 * detection cannot see: it refreshes the rows in the transaction and leaves
 * every other year's index cell showing the pre-edit ratio until the next
 * interaction repaints it (field report: editing 2026 moved 2027, 2025 stayed
 * stale). ``useRefreshCellsOnReload`` does the same after an Apply.
 */
function applyAndRefresh(api: GridApi, transaction: RowDataTransaction): void {
  api.applyTransaction(transaction);
  api.refreshCells({ force: true, suppressFlash: true });
}

const REFUSAL_TITLES: Record<NonNullable<GridWrite["kind"]> | "edit", string> =
  {
    edit: "Unable to apply the change",
    paste: "Unable to paste",
    delete: "Unable to clear the cells",
    copy: "Unable to copy"
  };

/** The refused cells as the target labels them, a few per toast. */
function describeRefusals(
  target: WriteTarget,
  metaData: ForecastMetaDataType | undefined,
  refusals: CellRefusal[]
): string {
  const shown = refusals.slice(0, 5).map(function describe({ change, reason }) {
    const header = target.columnLabel(change.colId);
    const { fiscal_year_name: year, metric_name: metric } = change.data;
    const kpi = metaData?.metricDisplayMapping[metric] ?? metric;
    return `${year} ${header} ${kpi}: ${reason}.`;
  });
  const more = refusals.length - shown.length;
  return [...shown, ...(more > 0 ? [`…and ${more} more.`] : [])].join("\n");
}

/** The local recompute as a mutation, so a refused edit toasts. */
function useLocalRecompute() {
  return useMutation({
    mutationFn: async ({
      write,
      args
    }: {
      write: GridWrite;
      args: Parameters<typeof applyCellChangesLocally>;
    }): Promise<ReturnType<typeof applyCellChangesLocally>> => {
      try {
        return applyCellChangesLocally(...args);
      } catch (error) {
        if (!(error instanceof CellEditsRefused)) throw error;
        throw new UserFacingError(
          describeRefusals(write.target, args[2], error.refusals),
          REFUSAL_TITLES[write.kind ?? "edit"]
        );
      }
    },
    meta: {
      errorMessage: "The change could not be recalculated and was not applied"
    }
  });
}

/** Puts the edited cell back to its value before the edit. */
function revertCell(api: GridApi, change: GridCellChange): void {
  const { fiscal_year_name, metric_name, producer_type } = change.data;
  const node = api.getRowNode(
    createRowId(fiscal_year_name, metric_name, producer_type)
  );
  if (!node?.data) return;
  applyAndRefresh(api, {
    update: [{ ...node.data, [change.colId]: change.oldValue }]
  });
}

/**
 * The one serial writer, and whether it is busy. `queueWrite` refuses while a
 * write is in flight — SPEC.md § The copy goes through the mutation queue.
 */
function useSerialWriter(
  processCellChanges: (write: GridWrite) => Promise<void>
) {
  const [isWriting, setIsWriting] = useState(false);
  // The synchronous truth behind `isWriting`, which lags a render.
  const writingRef = useRef(false);

  const runWrite = useCallback(
    async (write: GridWrite) => {
      writingRef.current = true;
      setIsWriting(true);
      // A failure is already the mutation's toast; here it only settles the write.
      const landed = await processCellChanges(write).then(
        () => true,
        () => false
      );
      writingRef.current = false;
      setIsWriting(false);
      write.onSettled?.(landed);
    },
    [processCellChanges]
  );

  const triggerBatch = useSerialAsync(runWrite);

  const queueWrite = useCallback(
    (write: GridWrite): boolean => {
      if (writingRef.current) return false;
      triggerBatch(write);
      return true;
    },
    [triggerBatch]
  );

  return { triggerBatch, queueWrite, isWriting };
}

/**
 * Runs every write, in order, each after the one before it settles, and
 * resolves whether it landed, as `onSettled` hears too. A page that edits
 * several targets in quick succession must land each one, where a grid's
 * retyping keeps the latest. `isWriting` holds while any write is queued or
 * running, so a save can wait for the cascades it would read.
 */
function useKeepAllLane(
  processCellChanges: (write: GridWrite) => Promise<void>
) {
  const laneRef = useRef<Promise<unknown>>(Promise.resolve());
  const [pending, setPending] = useState(0);
  const enqueueWrite = useCallback(
    (write: GridWrite): Promise<boolean> => {
      setPending((count) => count + 1);
      // A failure is already the mutation's toast; here it only settles the write.
      const landed = laneRef.current.then(() =>
        processCellChanges(write).then(
          () => true,
          () => false
        )
      );
      laneRef.current = landed;
      return landed.then((outcome) => {
        setPending((count) => count - 1);
        write.onSettled?.(outcome);
        return outcome;
      });
    },
    [processCellChanges]
  );
  return { enqueueWrite, isEnqueuedWriting: pending > 0 };
}

export function useWorkspaceMutationQueue({
  metaData,
  markdownStatus,
  forecastWeekFrom,
  saveCubeWindow,
  rowSink
}: UseWorkspaceMutationQueueOptions) {
  const { mutateAsync: recomputeLocally } = useLocalRecompute();

  const processCellChanges = useCallback(
    async (write: GridWrite) => {
      const { target, cellChanges } = write;
      const [first] = cellChanges;
      if (!first) return;
      const captureRows = rowSink ? rowSink() : () => {};
      // The producer whose rows RECEIVE the batch — the same one for an edit
      // (you type on your own row) and for a copy (it writes yours).
      const cellProducer = first.data.producer_type;
      // Fresh read, so the previous cycle's result is reflected in the payload.
      const rows = target.rows(cellProducer, metaData?.yearMapping);
      // The frame ends at the cube window: past it a cell is blank and the
      // cascade may not write there. Weeks before it stay, since the history
      // seeds the stock rolling.
      const transactionalData = cutPastWindowEnd(
        rows,
        metaData,
        saveCubeWindow
      );

      // Every write recomputes locally: the weekly cascade rolled up into the
      // aggregate columns from metaData.weekPeriodMapping. A throw is an edit
      // the grid should never have offered or cannot compute: it toasts, and
      // cells the grid already wrote go back to their values before the write.
      let local: ReturnType<typeof applyCellChangesLocally>;
      try {
        local = await recomputeLocally({
          write,
          args: [
            transactionalData,
            cellChanges,
            metaData,
            markdownStatus,
            forecastWeekFrom
          ]
        });
      } catch (error) {
        console.error("Client-side temporal recalc refused the edit", error);
        if (write.kind !== "copy")
          for (const change of [...cellChanges].reverse())
            target.revert(change);
        throw error;
      }
      target.apply(local.update);
      captureRows(local.update);
    },
    [
      recomputeLocally,
      metaData,
      markdownStatus,
      forecastWeekFrom,
      saveCubeWindow,
      rowSink
    ]
  );

  const { triggerBatch, queueWrite, isWriting } =
    useSerialWriter(processCellChanges);

  const { handleCellValueChanged, handleBatchStart, handleBatchEnd } =
    useCellEditBatcher(
      useCallback(
        ({ api, cellChanges, kind }: CellEditBatch) =>
          triggerBatch({ target: gridApiTarget(api), cellChanges, kind }),
        [triggerBatch]
      )
    );

  const { enqueueWrite, isEnqueuedWriting } =
    useKeepAllLane(processCellChanges);

  return {
    handleCellValueChanged,
    handleBatchStart,
    handleBatchEnd,
    queueWrite,
    enqueueWrite,
    isWriting: isWriting || isEnqueuedWriting
  };
}
